// Палитра: Ctrl+K как единственная точка входа во всё, что можно найти словами.
//
// ## Что она заменила
//
// До 04.09.2026 по Ctrl+K открывалось окно с одним полем — вопросом про деньги. Спросить
// «сколько висит на Багровском» было можно, а перейти в «Ведомости» нельзя: за экранами
// человек шёл мышью в полосу разделов, а потом ещё раз мышью во вкладку внутри раздела.
// Двадцать три экрана продукта, и до половины из них — два прицельных попадания мышью.
//
// Теперь одно поле отвечает на три разных вопроса человека, и он не обязан заранее знать,
// какой из них задаёт:
//
// | Напечатал | Получил |
// |---|---|
// | «вед», «зарплата» | экран «Расчёт · Ведомости» |
// | «Порубова» | карточку человека из реестра |
// | «сколько висит на Порубовой» | ответ помощника числом из базы |
//
// ## Почему список показан ДО первой буквы
//
// Пустая палитра с приглашением «начните печатать» — способ ничего не сообщить тому, кто
// открыл её впервые. Человек не знает ни что тут можно набрать, ни что бывает кроме
// вопросов. Поэтому сразу видно всё, что ему открыто по правам: палитра работает и как
// оглавление продукта.
//
// ## Почему поиск людей отдельной группой и с задержкой
//
// Экраны отбираются на месте, из списка в двадцать три строки. Люди живут на сервере, их
// 286, и запрос на каждую букву — это шесть запросов на слово «Порубова». Поэтому четверть
// секунды тишины перед запросом и не раньше второй буквы: по одной букве сервер вернул бы
// половину реестра, и полезного в этом ноль.
//
// ## Почему «Спросить» есть всегда
//
// Это единственная строка, которая не может не найтись. Человек, набравший вопрос словами,
// в списке экранов не найдёт ничего — и палитра, честно показавшая «ничего не нашлось»,
// оставила бы его в тупике ровно там, где у продукта есть ответ.

import { useEffect, useMemo, useState } from 'react';

import {
  RiArrowRightUpLine,
  RiCornerDownLeftLine,
  RiSearchLine,
  RiSparkling2Line,
  RiUserLine,
} from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import { ЗНАЧКИ_РАЗДЕЛОВ } from '@/app/значки-разделов';
import type { SectionSlug } from '@/app/sections';
import { sectionBySlug } from '@/app/sections';
import { useСреда } from '@/shared/среда';
import { AssistantDialog } from '@/shared/assistant/AssistantDialog';
import { useEmployeesQuery } from '@/modules/people/queries';
import { STATUS_TITLES } from '@/modules/people/dictionaries';
import { canAny } from '@/shared/rbac/permissions';
import { useAuthStore } from '@/shared/auth/store';
import * as Command from '@/shared/ui/command-menu';
import * as Kbd from '@/shared/ui/kbd';

import { путь } from './адреса';
import { доступныеАдреса } from './доступ';
import { отобрать } from './отбор';

/** Сочетание, открывающее палитру. На маке — Cmd, на Windows — Ctrl. */
function этоВызовПалитры(событие: KeyboardEvent): boolean {
  return событие.key.toLowerCase() === 'k' && (событие.metaKey || событие.ctrlKey);
}

/** Не раньше второй буквы и не чаще раза в четверть секунды — см. заголовок файла. */
const ПОРОГ_ПОИСКА = 2;
const ЗАДЕРЖКА_МС = 250;

function useОтложенный(значение: string, задержка: number): string {
  const [отложенное, положить] = useState(значение);
  useEffect(() => {
    const таймер = setTimeout(() => положить(значение), задержка);
    return () => clearTimeout(таймер);
  }, [значение, задержка]);
  return отложенное;
}

export function Палитра() {
  const [открыта, открыть] = useState(false);
  const [вопросПомощнику, задатьВопросПомощнику] = useState<string | null>(null);
  const [запрос, печатать] = useState('');
  const [выбрано, выбрать] = useState('');
  const навигация = useNavigate();
  const адресСейчас = useLocation().pathname;
  const права = useAuthStore((состояние) => состояние.permissions);

  // Пока палитра закрыта, значение поля живёт: человек открыл, передумал, открыл снова —
  // и его старый запрос ему уже не нужен. Чистим при каждом открытии, а не при закрытии:
  // при закрытии поле мигнёт пустым на глазах у закрывающей анимации.
  useEffect(() => {
    if (открыта) {
      печатать('');
    }
  }, [открыта]);

  useEffect(() => {
    function поКлавише(событие: KeyboardEvent) {
      if (!этоВызовПалитры(событие)) {
        return;
      }
      // Отменяем поиск браузера: у Ctrl+K он свой, и без отмены откроются оба.
      событие.preventDefault();
      открыть((было) => !было);
    }
    window.addEventListener('keydown', поКлавише);
    return () => window.removeEventListener('keydown', поКлавише);
  }, []);

  const доступные = useMemo(() => доступныеАдреса(права), [права]);

  const искатьЛюдей = canAny(права, ['core.employee.view']);
  const отложенный = useОтложенный(запрос.trim(), ЗАДЕРЖКА_МС);
  // Условие начинается с «палитра открыта»: поле не чистится при закрытии (иначе оно мигнёт
  // пустым на глазах у закрывающегося окна), и без этой проверки закрытая палитра продолжала
  // бы дёргать сервер поиском по последнему набранному.
  const ищемЛюдей = открыта && искатьЛюдей && отложенный.length >= ПОРОГ_ПОИСКА;
  const люди = useEmployeesQuery(ищемЛюдей ? { q: отложенный, limit: 6 } : {});
  // Пока запрос не дорос до порога, хук всё равно вызван — правило хуков не знает условий, —
  // но его ответ не показывается: иначе в палитре висел бы первый десяток реестра, не
  // имеющий к набранному никакого отношения.
  const найденныеЛюди = ищемЛюдей ? (люди.data?.items ?? []) : [];
  const ищем = ищемЛюдей && люди.isFetching && найденныеЛюди.length === 0;

  const очищенный = запрос.trim();

  const найденныеЭкраны = useMemo(
    () => отобрать(доступные, очищенный, (адресСейчас.split('/')[1] ?? '') as SectionSlug),
    [доступные, очищенный, адресСейчас],
  );

  // Какая строка откроется по Enter. Порядок групп смысловой: сначала места, потом люди,
  // потом вопрос.
  //
  // Держать это руками пришлось из-за задержки поиска людей. Библиотека выбирает первую
  // строку в момент набора — а в этот момент людей ещё нет, и первой оказывается «Спросить».
  // Через полсекунды человек появляется НАД ней, но выбор остаётся на вопросе: набрал
  // фамилию, нажал Enter — и вместо карточки уехал в помощника. Видно это только глазами на
  // кадре, никакая проверка вёрстки такого не ловит.
  const первая =
    найденныеЭкраны[0] !== undefined
      ? путь(найденныеЭкраны[0].адрес)
      : найденныеЛюди[0] !== undefined
        ? `/people/employees/r/${найденныеЛюди[0].id}`
        : 'спросить помощника';

  // Зависимость ровно одна — сама первая строка. Пока она не поменялась, выбор не трогаем:
  // иначе стрелка вниз сбрасывалась бы обратно на первую строку при каждой перерисовке.
  useEffect(() => {
    выбрать(первая);
  }, [первая]);

  const среда = useСреда();
  // Узкая полоса разделов — это «средне» и «узко» вместе: там кнопка становится значком с
  // подписью под ним, и длинному тексту места нет.
  const узкаяПолоса = среда.ширина !== 'широко';

  function перейти(адрес: string) {
    открыть(false);
    void навигация(адрес);
  }

  return (
    <>
      <button
        type='button'
        onClick={() => открыть(true)}
        // Доступное имя длиннее видимого и включает его: на узкой полосе разделов полная
        // формулировка не помещается, а обрезанная не объясняет, что тут ищут.
        aria-label='Найти экран или человека, спросить словами'
        className='flex w-full items-center gap-2.5 rounded-10 px-3 py-2 text-label-sm text-text-sub-600 transition duration-200 ease-out hover:bg-bg-weak-50 hover:text-text-strong-950 тесно:min-h-14 тесно:flex-col тесно:justify-center тесно:gap-1 тесно:px-1 тесно:py-1.5 тесно:text-center тесно:text-paragraph-xs тесно:leading-tight'
      >
        <RiSearchLine className='size-5 shrink-0 text-text-soft-400' />
        {/* Сочетание — ВТОРОЙ строкой, а не рядом с подписью.
            Замер 04.09.2026: подписи «Найти и спросить» нужно 122 точки, а рядом с «Ctrl K»
            ей остаётся 119 — и она обрезалась в «Найти и спрос…». Три точки. Выиграть их
            можно было только за счёт полей или зазора, а они здесь не случайные: текст на
            кнопке стоит ровно на той же вертикали, что названия разделов выше (12 + 12 + 20
            + 10 = 54). Ради трёх точек ломать эту вертикаль — менять заметное на незаметное.
            Сочетание при этом остаётся написанным: без него о нём не узнают. */}
        {/* Подпись выбирается КОДОМ, а не прячется классами.
            Спрятанный вариант остаётся в разметке: он уходит в чтение с экрана и в текстовое
            содержимое кнопки. 10.09.2026 из-за этого сквозная проверка увидела на кнопке
            «Найти и спроситьНайтиCtrl K» — три подписи разом, — и справедливо назвала это
            повтором. */}
        <span className='flex min-w-0 flex-1 flex-col items-start тесно:flex-none тесно:items-center'>
          {/* В 72 точки «Найти и спросить» не помещается ни при каком размере шрифта, а
              обрезанное «Найти и…» не отвечает ни на один вопрос. Остаётся глагол. */}
          <span className='w-full truncate text-left'>
            {узкаяПолоса ? 'Найти' : 'Найти и спросить'}
          </span>
          {/* Сочетание — только там, где его есть чем нажать. У экранной клавиатуры планшета
              клавиши Ctrl нет вовсе, и «Ctrl K» на ней — инструкция к несуществующему. */}
          {среда.клавиатура && !узкаяПолоса && (
            <span className='text-paragraph-xs text-text-soft-400'>Ctrl K</span>
          )}
        </span>
      </button>

      <Command.Dialog
        open={открыта}
        onOpenChange={открыть}
        // Иначе список зачитывается вслух как «Suggestions»: собственная подпись библиотеки,
        // по-английски, в продукте, где ни одного английского слова человек видеть не должен.
        label='Что можно открыть'
        // Отбор свой (`отбор.ts`), библиотечный выключен. Три причины, все найдены проверкой.
        //
        // Первая — качество совпадения на русском: подробности в заголовке `отбор.ts`.
        //
        // Вторая — порядок групп. Библиотека сортирует и строки, и ГРУППЫ по совпадению. На
        // «вед» строка «Спросить: вед» совпадает не хуже «Ведомостей» и всплывает наверх, то
        // есть Enter сразу после набора отправлял бы вопрос вместо того, чтобы открыть экран.
        // Порядок групп здесь смысловой: сначала места, потом люди, потом вопрос.
        //
        // Третья — люди. Их отобрал сервер, в том числе по табельному номеру и телефону,
        // которых в имени нет: второй отбор на месте выбросил бы ровно тех, кого нашли не по
        // фамилии. Обойти это `forceMount`-ом не выходит — библиотека не регистрирует такие
        // строки вовсе, и до них не доходят стрелки: мышь снова обязательна.
        shouldFilter={false}
        value={выбрано}
        onValueChange={выбрать}
      >
        <Command.DialogTitle className='sr-only'>Найти и спросить</Command.DialogTitle>
        <Command.DialogDescription className='sr-only'>
          Название экрана, фамилия человека или вопрос про деньги словами.
        </Command.DialogDescription>

        <div className='group/cmd-input flex items-center gap-2 px-5 py-3'>
          <RiSearchLine className='size-5 shrink-0 text-text-soft-400' />
          <Command.Input
            value={запрос}
            onValueChange={печатать}
            autoFocus
            placeholder='Экран, фамилия или вопрос про деньги'
          />
        </div>

        <Command.List>
          {найденныеЭкраны.length > 0 && (
            <Command.Group heading='Перейти'>
              {найденныеЭкраны.map(({ адрес }) => {
                const раздел = sectionBySlug(адрес.раздел);
                const Значок = ЗНАЧКИ_РАЗДЕЛОВ[адрес.раздел];
                return (
                  <Command.Item
                    key={путь(адрес)}
                    value={путь(адрес)}
                    onSelect={() => перейти(путь(адрес))}
                  >
                    <Command.ItemIcon as={Значок} />
                    <span className='flex-1 truncate'>{адрес.название}</span>
                    {/* «Настройки · Настройки» — строка, которая дважды говорит одно и то же и
                      ничем не помогает. Раздел подписывается, только когда он добавляет
                      сведения к названию экрана. */}
                    {раздел !== undefined && раздел.title !== адрес.название && (
                      <span className='shrink-0 text-paragraph-xs text-text-soft-400'>
                        {раздел.title}
                      </span>
                    )}
                  </Command.Item>
                );
              })}
            </Command.Group>
          )}

          {(найденныеЛюди.length > 0 || ищем) && (
            <Command.Group heading='Люди'>
              {ищем ? (
                // Скелетом здесь была бы ложь о числе строк: сколько человек найдётся,
                // неизвестно. Одна честная строка вместо трёх выдуманных.
                <div className='flex items-center gap-3 px-3 py-2.5 text-paragraph-sm text-text-soft-400'>
                  <RiUserLine className='size-5 shrink-0' />
                  Ищу среди людей…
                </div>
              ) : (
                найденныеЛюди.map((человек) => (
                  <Command.Item
                    key={человек.id}
                    value={`/people/employees/r/${человек.id}`}
                    onSelect={() => перейти(`/people/employees/r/${человек.id}`)}
                  >
                    <Command.ItemIcon as={RiUserLine} />
                    <span className='flex-1 truncate'>{человек.display_name}</span>
                    <span className='shrink-0 truncate text-paragraph-xs text-text-soft-400'>
                      {человек.site_name ?? STATUS_TITLES[человек.status]}
                    </span>
                  </Command.Item>
                ))
              )}
            </Command.Group>
          )}

          <Command.Group heading='Спросить'>
            <Command.Item
              // Единственная строка, которая обязана быть видна всегда: см. заголовок файла.
              // Своим отбором это и обеспечено — строка просто рисуется без условий.
              value='спросить помощника'
              onSelect={() => {
                открыть(false);
                задатьВопросПомощнику(очищенный.length === 0 ? '' : очищенный);
              }}
            >
              <Command.ItemIcon as={RiSparkling2Line} />
              <span className='flex-1 truncate'>
                {очищенный.length === 0 ? 'Спросить про деньги словами' : `Спросить: ${очищенный}`}
              </span>
              <RiArrowRightUpLine className='size-4 shrink-0 text-text-soft-400' />
            </Command.Item>
          </Command.Group>
        </Command.List>

        <Command.Footer>
          <span className='text-paragraph-xs text-text-soft-400'>
            Стрелками выбрать, Enter открыть
          </span>
          <div className='flex items-center gap-1.5'>
            <Kbd.Root>↑</Kbd.Root>
            <Kbd.Root>↓</Kbd.Root>
            <Kbd.Root>
              <RiCornerDownLeftLine className='size-3' />
            </Kbd.Root>
            <Kbd.Root>esc</Kbd.Root>
          </div>
        </Command.Footer>
      </Command.Dialog>

      <AssistantDialog вопрос={вопросПомощнику} закрыть={() => задатьВопросПомощнику(null)} />
    </>
  );
}
